import * as React from 'react';
import { useRouter } from 'next/router';
import { HiXMark } from 'react-icons/hi2';
import Link from 'components/Link';
import { AuthStatus } from 'constants/auth';
import { SETTINGS_PAGE } from 'constants/pages';
import { PastDueNoticeSubscription } from 'constants/payloads/dunning';
import useViewer from 'hooks/useViewer';
import { getIsNativePlatform } from 'utils/client/mobile';
import Sentry from 'utils/shared/sentry';
import { formatMoney, PastDueOfferRequestError } from './pastDueOffer';
import {
  clearPastDueNoticeCache,
  loadPastDueNotice,
  openBillingPortal,
  restoreAccessAfterPayment,
} from './pastDueNotice';

/**
 * The in-app half of the failed-renewal dunning emails: one bar at the top of every page while a
 * renewal is overdue, saying so plainly and offering the two things that fix it. Access is
 * already paused by then (the JWT claim goes with `past_due`), so the app pages show a paywall,
 * and this is the only place inside the product that tells them why.
 *
 * Mounted in the Navbar, and at the top of `SidebarPage` on the app pages that hide the Navbar
 * (flow, news, the exposure pages, the calculator for a signed-in user). `isStatic` there: the bar
 * sits in the normal flow and needs no body offset.
 *
 * Not shown on Settings, where the past-due row carries the same actions plus the offer, and not
 * shown in the native apps at all: every payment surface in this app is gated on
 * `!getIsNativePlatform()` (the Settings billing section included), and a "Pay now" link out to
 * Stripe is exactly what that gate exists to keep out of the store builds. Those customers still
 * get the dunning emails.
 *
 * Dismissable for the browser session only: it comes back next visit until the invoice is paid.
 */

export const DISMISS_KEY_PREFIX = '@@ifinance/past-due-banner-dismissed:';

/** Coming back to the tab asks again at most this often. */
export const RECHECK_THROTTLE_MS = 15 * 1000;
/** One more look after a return that still found it overdue: the payment may be landing. */
export const RECHECK_FOLLOW_UP_MS = 20 * 1000;

/**
 * Set when Pay now or Update card is clicked. The billing portal returns to the app with a full
 * page load rather than a return to the tab, so the page that loads reads this to still take its
 * one follow-up look (or to apply the claims, when the answer is already clear). Per tab, and only
 * for a while: a customer who walked away from the portal is not looked for again later.
 */
export const PAYMENT_STARTED_KEY_PREFIX = '@@ifinance/past-due-payment-started:';
export const PAYMENT_STARTED_TTL_MS = 30 * 60 * 1000;

const dismissKey = (subscriptions: PastDueNoticeSubscription[]) =>
  `${DISMISS_KEY_PREFIX}${subscriptions
    .map((row) => row.invoiceId || row.externalSubscriptionId)
    .join('+')}`;

const readDismissed = (key: string) => {
  try {
    return window.sessionStorage.getItem(key) === '1';
  } catch {
    return false;
  }
};

const writeDismissed = (key: string) => {
  try {
    window.sessionStorage.setItem(key, '1');
  } catch {
    // Without storage the dismissal lasts until the next page; acceptable.
  }
};

const paymentStartedKey = (uid: string) => `${PAYMENT_STARTED_KEY_PREFIX}${uid}`;

const markPaymentStarted = (uid: string) => {
  try {
    window.sessionStorage.setItem(paymentStartedKey(uid), String(Date.now()));
  } catch {
    // Without storage a payment is still seen on the next return to the tab.
  }
};

const readPaymentStarted = (uid: string) => {
  try {
    const at = Number(window.sessionStorage.getItem(paymentStartedKey(uid)));
    return at > 0 && Date.now() - at < PAYMENT_STARTED_TTL_MS;
  } catch {
    return false;
  }
};

const forgetPaymentStarted = (uid: string) => {
  try {
    window.sessionStorage.removeItem(paymentStartedKey(uid));
  } catch {
    // Nothing to forget.
  }
};

/** True when a row on screen is missing from the new answer: that plan was paid (or ended). */
const anyCleared = (shown: PastDueNoticeSubscription[], next: PastDueNoticeSubscription[]) =>
  shown.some(
    (row) => !next.some((still) => still.externalSubscriptionId === row.externalSubscriptionId),
  );

/** "Real-time Options Dashboard" · "Real-time Options Dashboard and Technical Analysis System" */
export const joinNames = (names: string[]) =>
  names.length <= 1
    ? names[0] || ''
    : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;

const PastDueBanner: React.FC<{ isStatic?: boolean }> = ({ isStatic }) => {
  const viewer = useViewer();
  const router = useRouter();
  // Gated before the fetch, so the native apps do not even ask.
  const uid =
    viewer.status === AuthStatus.User && !getIsNativePlatform() ? viewer.viewer?.uid : undefined;

  const [subscriptions, setSubscriptions] = React.useState<PastDueNoticeSubscription[]>([]);
  const [dismissed, setDismissed] = React.useState(false);
  const [isOpeningPortal, setIsOpeningPortal] = React.useState(false);
  const barRef = React.useRef<HTMLDivElement>(null);
  // The rows on screen, so a recheck can tell which of them were paid since.
  const shownRef = React.useRef<PastDueNoticeSubscription[]>([]);

  // Every answer, first load or recheck, goes through here. A dismissal holds for the same
  // invoices; a different set (another plan fell overdue, an invoice was reissued) shows again.
  const applyNotice = React.useCallback((next: PastDueNoticeSubscription[]) => {
    const sameNotice = next.length > 0 && dismissKey(next) === dismissKey(shownRef.current);
    shownRef.current = next;
    setSubscriptions(next);
    setDismissed(
      (was) => next.length > 0 && ((was && sameNotice) || readDismissed(dismissKey(next))),
    );
  }, []);

  React.useEffect(() => {
    if (!uid) {
      applyNotice([]);
      return undefined;
    }
    let cancelled = false;
    loadPastDueNotice(uid)
      .then((notice) => {
        if (cancelled) return;
        applyNotice(notice.subscriptions);
        // Back from the portal already paid: the page's claims may predate the payment.
        if (notice.subscriptions.length === 0 && readPaymentStarted(uid)) {
          forgetPaymentStarted(uid);
          void restoreAccessAfterPayment(viewer);
        }
      })
      .catch(() => {
        // A failed read must never break the navbar; the emails and Settings still carry the notice.
        if (!cancelled) applyNotice([]);
      });
    return () => {
      cancelled = true;
    };
  }, [uid, viewer, applyNotice]);

  // While a renewal is overdue, coming back to the tab asks again: the customer has usually just
  // paid in the Stripe tab the bar opened. A plan that comes back paid restores its access without
  // a sign-out, even while another is still overdue. Nothing listens while nothing is overdue, so
  // this costs the 99% nothing.
  const overdue = subscriptions.length > 0;
  React.useEffect(() => {
    if (!uid || !overdue) return undefined;
    let stopped = false;
    let lastCheck = 0;
    let followUp: ReturnType<typeof setTimeout> | undefined;
    const scheduleFollowUp = () => {
      if (stopped || followUp) return;
      followUp = setTimeout(() => {
        followUp = undefined;
        forgetPaymentStarted(uid);
        recheck(false);
      }, RECHECK_FOLLOW_UP_MS);
    };
    const recheck = (withFollowUp: boolean) => {
      if (stopped || document.visibilityState === 'hidden') return;
      const at = Date.now();
      if (withFollowUp && at - lastCheck < RECHECK_THROTTLE_MS) return;
      lastCheck = at;
      clearPastDueNoticeCache(uid);
      loadPastDueNotice(uid)
        .then((notice) => {
          if (stopped) return;
          const paid = anyCleared(shownRef.current, notice.subscriptions);
          applyNotice(notice.subscriptions);
          if (paid) void restoreAccessAfterPayment(viewer);
          if (notice.subscriptions.length === 0) {
            forgetPaymentStarted(uid);
          } else if (withFollowUp) {
            scheduleFollowUp();
          }
        })
        .catch(() => {
          // The bar stays as it was. The connection may just not be back yet (a laptop waking),
          // so the one follow-up still happens.
          if (withFollowUp) scheduleFollowUp();
        });
    };
    // Loaded fresh from paying elsewhere (the portal returns with a full page load, not a return
    // to the tab): take the same one follow-up look a return would have.
    if (readPaymentStarted(uid)) scheduleFollowUp();
    const onReturn = () => recheck(true);
    window.addEventListener('focus', onReturn);
    document.addEventListener('visibilitychange', onReturn);
    return () => {
      stopped = true;
      if (followUp) clearTimeout(followUp);
      window.removeEventListener('focus', onReturn);
      document.removeEventListener('visibilitychange', onReturn);
    };
  }, [uid, overdue, viewer, applyNotice]);

  const onSettingsPage = router?.pathname === SETTINGS_PAGE;
  const visible = subscriptions.length > 0 && !dismissed && !onSettingsPage;

  // The header is fixed: while the bar is in it, the page needs that much more room at the top
  // or the bar covers the first line of content. A static header pushes content on its own.
  React.useEffect(() => {
    if (!visible || isStatic) return undefined;
    const bar = barRef.current;
    if (!bar) return undefined;
    const apply = () => {
      document.body.style.paddingTop = `${bar.offsetHeight}px`;
    };
    apply();
    const observer =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => apply());
    observer?.observe(bar);
    return () => {
      observer?.disconnect();
      document.body.style.paddingTop = '';
    };
  }, [visible, isStatic]);

  if (!visible) return null;

  const single = subscriptions.length === 1 ? subscriptions[0] : null;
  const names = joinNames(subscriptions.map((row) => row.displayName));
  const payHref = single?.hostedInvoiceUrl || SETTINGS_PAGE;
  const payLabel =
    single && single.amountDueCents !== null && single.hostedInvoiceUrl
      ? `Pay ${formatMoney(single.amountDueCents, single.currency)} now`
      : 'Pay now';

  const forgetNotice = () => {
    if (!uid) return;
    clearPastDueNoticeCache(uid);
    markPaymentStarted(uid);
  };

  const handleUpdateCard = () => {
    if (isOpeningPortal) return;
    setIsOpeningPortal(true);
    forgetNotice();
    openBillingPortal().catch((error: PastDueOfferRequestError) => {
      Sentry.captureException(error);
      setIsOpeningPortal(false);
      // Only a message the portal route itself sent is worth an alert; a dropped connection just
      // re-enables the button.
      if (error?.statusCode && error.message) {
        alert(error.message);
      }
    });
  };

  const handleDismiss = () => {
    writeDismissed(dismissKey(subscriptions));
    setDismissed(true);
  };

  return (
    <div
      ref={barRef}
      role="region"
      aria-label="Payment overdue"
      data-testid="past-due-banner"
      className="relative z-50 flex flex-wrap items-center justify-center gap-x-3 gap-y-1.5 bg-gradient-to-r from-red-900 via-red-800 to-red-900 py-2 pl-3 pr-10 text-xs font-medium text-white shadow-lg shadow-red-900/30 sm:px-4 sm:pr-12 sm:text-sm"
    >
      <span className="text-center">
        <span className="font-bold">
          Your {names} renewal{subscriptions.length > 1 ? 's' : ''} didn&apos;t go through.
        </span>{' '}
        <span className="hidden sm:inline">
          Access is paused until {subscriptions.length > 1 ? 'they are' : 'it is'} paid.
        </span>
      </span>
      <span className="flex flex-wrap items-center justify-center gap-2">
        <a
          href={payHref}
          target={single?.hostedInvoiceUrl ? '_blank' : undefined}
          rel={single?.hostedInvoiceUrl ? 'noopener noreferrer' : undefined}
          onClick={forgetNotice}
          className="whitespace-nowrap rounded-full bg-white px-3 py-0.5 text-xs font-bold uppercase tracking-wide text-red-900 transition-colors hover:bg-red-100"
        >
          {payLabel}
        </a>
        <button
          type="button"
          onClick={handleUpdateCard}
          disabled={isOpeningPortal}
          className="whitespace-nowrap rounded-full border border-white/60 px-3 py-0.5 text-xs font-bold uppercase tracking-wide text-white transition-colors hover:bg-white/15 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {isOpeningPortal ? 'Opening…' : 'Update card'}
        </button>
        <Link href={SETTINGS_PAGE} className="whitespace-nowrap underline underline-offset-2">
          Details
        </Link>
      </span>
      <button
        type="button"
        onClick={handleDismiss}
        aria-label="Dismiss for now"
        className="absolute right-1 top-1/2 -translate-y-1/2 rounded-full p-1.5 text-white/80 hover:bg-white/15 hover:text-white sm:right-2"
      >
        <HiXMark aria-hidden="true" className="h-4 w-4 sm:h-5 sm:w-5" />
      </button>
    </div>
  );
};

export default PastDueBanner;
